import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Typography,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import moment from "moment";
import UserServices from "../../services/UserServices";
import { useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";

const { Title, Text } = Typography;
const { Option } = Select;

const ModalDownloadResume = ({ visible, onClose, formatFile }) => {
  const { username } = useParams();
  const [active, setActive] = useState(false);
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(1, "month"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const [dateRange, setDateRange] = useState(
    `${moment(dateFrom).format("YYYY-MM-DD")}:${moment(dateTo).format(
      "YYYY-MM-DD"
    )}`
  );
  const [type, setType] = useState("");
  const [measureType, setMeasureType] = useState("");
  const [loading, setLoading] = useState(false);
  const [, forceUpdate] = useState();
  const [errorMessage, setErrorMessage] = useState("");

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => (
        <div className="text-red-600 pt-2 text-xs">{message}</div>
      ),
      messages: {
        required: "Campo requerido",
        dateMaxRange:
          "Las fechas de inicio y finalización no pueden exceder los 6 meses.",
      },
      validators: {
        isSameOrBefore: {
          rule: (val, params, validator) => {
            return moment(val).isSameOrBefore(moment());
          },
        },
        dateMaxRange: {
          rule: (val, params, validator) => {
            const dates = val.split(":");
            const diff = moment(dates[1]).diff(dates[0], "months", true);
            return diff <= 6;
          },
        },
      },
    })
  );

  const disabled = () => !simpleValidator.current.allValid();

  useEffect(() => {
    if (visible !== null) {
      setType("");
      setActive(visible);
    }
  }, [visible]);

  const handleDownload = async () => {
    const from = dateFrom
      ? moment(dateFrom).format("YYYY-MM-DD")
      : moment().subtract(1, "month").format("YYYY-MM-DD");
    const to = dateTo
      ? moment(dateTo).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await UserServices.getResumeFileUrl(
        from,
        to,
        username,
        type,
        measureType,
        formatFile
      );
      if (response.status >= 200 && response.status < 300) {
        const link = document.createElement("a");
        link.href = response.data.url;
        document.body.appendChild(link);
        link.setAttribute(
          "download",
          response.data.url.substr(response.data.url.lastIndexOf("/") + 1)
        );
        link.click();
        document.body.removeChild(link);
        if (onClose) {
          setTimeout(onClose, 1000);
        }
      } else if (response.status >= 400) {
        setErrorMessage(response.data.message);
      }
    } catch (error) {
      console.log(error);
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
    setDateRange((v) => {
      const d = v.split(":");
      if (name === "dateFrom") {
        return `${moment(value).format("YYYY-MM-DD")}:${d[1]}`;
      }
      return `${d[0]}:${moment(value).format("YYYY-MM-DD")}`;
    });
    simpleValidator.current.showMessageFor(name);
    simpleValidator.current.showMessageFor("dateRange");
  };

  const resetForm = () => {
    setDateFrom(new Date(moment().subtract(1, "month")));
    setDateTo(new Date());
    setType("");
    setErrorMessage("");
    setDateRange(
      `${moment(dateFrom).format("YYYY-MM-DD")}:${moment(dateTo).format(
        "YYYY-MM-DD"
      )}`
    );
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  if (!active) {
    return null;
  }

  return (
    <Modal
      open={active}
      title={`${translate(
        "Informe del paciente"
      )} (${formatFile.toUpperCase()})`}
      onOk={null}
      onCancel={() => {
        if (onClose) {
          resetForm();
          onClose();
        }
      }}
      footer={false}
    >
      <Form onFinish={handleDownload}>
        <Row className="my-6" gutter={40}>
          <Col span={24} className="text-center">
            <Text>{translate("Informe a descargar")}</Text>
          </Col>
          <Col
            span={16}
            offset={4}
            className="mb-8 flex flex-row justify-center"
          >
            <Select
              value={type}
              onChange={(value) => {
                setType(value);
              }}
              className="border-custom_primary w-full text-center items-center"
              options={[
                { value: "", label: "Seleccione..." },
                { value: "medicion", label: translate("Medición") },
                { value: "alimentacion", label: translate("Alimentación") },
                { value: "medicamentos", label: translate("Medicamentos") },
                { value: "notes", label: translate("Notas") },
                {
                  value: "physical_activity",
                  label: translate("Actividad Física"),
                },
              ]}
            />

            {simpleValidator.current.message(
              "type",
              type,
              "required",
              get_language === "es"
                ? {
                    messages: {
                      required: "Seleccione un tipo de informe",
                    },
                  }
                : {}
            )}
          </Col>
          <Col span={24} className="mb-2">
            <Title
              level={5}
              className="text-center"
              style={{ fontWeight: "normal" }}
            >
              {translate(
                "Selecciona el plazo de tiempo para el que desea emitir el informe de salud."
              )}
            </Title>
          </Col>
          <Col span={16} offset={4} className="flex flex-row space-x-10">
            <div className="flex flex-1 flex-col items-right">
              <div>{translate("Fecha de inicio")}</div>
              <CustomDatePicker
                locale={get_language}
                selected={dateFrom}
                onChange={(e) => onChangeDatePicker("dateFrom", e)}
              />

              {simpleValidator.current.message(
                "dateFrom",
                dateFrom,
                "required",
                get_language === "es"
                  ? {
                      messages: {
                        required: "Seleccione un tipo de informe",
                      },
                    }
                  : {}
              )}
            </div>
            <div className="flex flex-1 flex-col items-stretch">
              <div>{translate("Fecha finalización")}</div>
              <CustomDatePicker
                locale={get_language}
                selected={dateTo}
                onChange={(e) => onChangeDatePicker("dateTo", e)}
                maxDate={new Date()}
              />

              {simpleValidator.current.message(
                "dateTo",
                dateTo,
                "required|isSameOrBefore",
                get_language === "es"
                  ? {
                      messages: {
                        required: "Seleccione una fecha de finalización",
                        isSameOrBefore:
                          "La fecha finalización debe ser igual o anterior a la de hoy",
                      },
                    }
                  : {}
              )}
            </div>
          </Col>
          <Col span={24} className="text-center mb-8">
            {simpleValidator.current.message(
              "dateRange",
              dateRange,
              "dateMaxRange",
              get_language === "es"
                ? {
                    messages: {
                      dateMaxRange:
                        "Las fechas de inicio y finalización no pueden exceder los 6 meses.",
                    },
                  }
                : {}
            )}
          </Col>
          {type === "medicion" && (
            <>
              <Col span={24} className="text-center">
                <Text>{translate("Tipo de medición")}</Text>
              </Col>
              <Col
                span={16}
                offset={4}
                className="mb-8 flex flex-row justify-center"
              >
                <Select
                  value={measureType}
                  onChange={(value) => {
                    setMeasureType(value);
                  }}
                  className="border-custom_primary w-full text-center items-center"
                  options={[
                    { value: "", label: "Todas" },
                    {
                      value: "hypertension",
                      label: translate("Presión Arterial"),
                    },
                    { value: "diabetes", label: translate("Glucemia") },
                    { value: "weight", label: translate("Peso") },
                    {
                      value: "o2saturation",
                      label: translate("Saturación O2"),
                    },
                    { value: "temperature", label: translate("Temperatura") },
                    { value: "water", label: translate("Agua") },
                  ]}
                />
              </Col>
            </>
          )}
          {errorMessage && (
            <Col
              span={24}
              className="flex flex-row justify-center mt-2 text-custom_red"
            >
              {errorMessage}
            </Col>
          )}
          <Col span={24} className="flex flex-row justify-center mt-10">
            <Button
              htmlType="submit"
              type="primary"
              className="bg-custom_primary"
              disabled={disabled() || loading}
              loading={loading}
            >
              {translate("Descargar")} {formatFile.toUpperCase()}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalDownloadResume;
